import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../../services/api";
import { DashboardShell } from "../../components/dashboard/DashboardKit";
import {
  Home, FolderOpen, PlusCircle,
  FileText, Calendar, Star, Users,
  ArrowLeft, ArrowRight,
  Hourglass, XCircle, CheckCircle2, BarChart3, Flag,
  Hash, ClipboardList,
} from "lucide-react";

// ── Nav + Stats (UNCHANGED) ───────────────────────────────────────────────────
const engineerNav = [
  { icon: <Home className="w-5 h-5" />, label: "Dashboard",   path: "/engineer"          },
  { icon: <FolderOpen className="w-5 h-5" />, label: "My Projects", path: "/engineer/projects"  },
   {
    icon: <PlusCircle className="w-5 h-5" />,
    label: "Add Project",
    path: "/engineer/add-project"
  }
];

const engineerStats = [
  { label: "Assigned", value: "04", className: "text-ap-violetLight" },
  { label: "Tasks",    value: "07", className: "text-ap-orange"      },
  { label: "Reports",  value: "12", className: "text-ap-red"         },
];

// ─────────────────────────────────────────────────────────────────────────────
//  DYNAMIC THEME SYSTEM
//  - 20 distinct palettes — repetition only starts at project #21
//  - themeIndex = project.id % THEMES.length  →  stable, same id = same colour
//  - NO database changes, NO backend changes, NO API changes
// ─────────────────────────────────────────────────────────────────────────────
const THEMES = [
  {
    // 0 — Violet (Asian Paints brand)
    primary:     "#5C2D91",
    primaryDark: "#3D1870",
    primaryMid:  "#7B3FB5",
    light:       "#EDE6F7",
    lighter:     "#F7F2FD",
    text:        "#5C2D91",
    gradient:    "linear-gradient(135deg,#3D1870 0%,#5C2D91 50%,#7B3FB5 100%)",
    gradientBar: "linear-gradient(90deg,#5C2D91,#7B3FB5)",
    shadowRgb:   "92,45,145",
  },
  {
    // 1 — Amber orange
    primary:     "#F58220",
    primaryDark: "#C4600A",
    primaryMid:  "#F5A050",
    light:       "#FFF4E8",
    lighter:     "#FFFBF5",
    text:        "#C4600A",
    gradient:    "linear-gradient(135deg,#C4600A 0%,#F58220 50%,#F5A050 100%)",
    gradientBar: "linear-gradient(90deg,#F58220,#F5A050)",
    shadowRgb:   "245,130,32",
  },
  {
    // 2 — Emerald green
    primary:     "#22C55E",
    primaryDark: "#15803D",
    primaryMid:  "#4ADE80",
    light:       "#ECFDF5",
    lighter:     "#F0FFF6",
    text:        "#15803D",
    gradient:    "linear-gradient(135deg,#15803D 0%,#22C55E 50%,#4ADE80 100%)",
    gradientBar: "linear-gradient(90deg,#22C55E,#4ADE80)",
    shadowRgb:   "34,197,94",
  },
  {
    // 3 — Crimson red
    primary:     "#EF4444",
    primaryDark: "#B91C1C",
    primaryMid:  "#F87171",
    light:       "#FEF2F2",
    lighter:     "#FFF5F5",
    text:        "#B91C1C",
    gradient:    "linear-gradient(135deg,#B91C1C 0%,#EF4444 50%,#F87171 100%)",
    gradientBar: "linear-gradient(90deg,#EF4444,#F87171)",
    shadowRgb:   "239,68,68",
  },
  {
    // 4 — Sky blue
    primary:     "#0EA5E9",
    primaryDark: "#0369A1",
    primaryMid:  "#38BDF8",
    light:       "#F0F9FF",
    lighter:     "#F5FBFF",
    text:        "#0369A1",
    gradient:    "linear-gradient(135deg,#0369A1 0%,#0EA5E9 50%,#38BDF8 100%)",
    gradientBar: "linear-gradient(90deg,#0EA5E9,#38BDF8)",
    shadowRgb:   "14,165,233",
  },
  {
    // 5 — Deep indigo
    primary:     "#4F46E5",
    primaryDark: "#3730A3",
    primaryMid:  "#818CF8",
    light:       "#EEF2FF",
    lighter:     "#F5F7FF",
    text:        "#3730A3",
    gradient:    "linear-gradient(135deg,#3730A3 0%,#4F46E5 50%,#818CF8 100%)",
    gradientBar: "linear-gradient(90deg,#4F46E5,#818CF8)",
    shadowRgb:   "79,70,229",
  },
  {
    // 6 — Hot pink / rose
    primary:     "#EC4899",
    primaryDark: "#9D174D",
    primaryMid:  "#F472B6",
    light:       "#FDF2F8",
    lighter:     "#FFF5FB",
    text:        "#9D174D",
    gradient:    "linear-gradient(135deg,#9D174D 0%,#EC4899 50%,#F472B6 100%)",
    gradientBar: "linear-gradient(90deg,#EC4899,#F472B6)",
    shadowRgb:   "236,72,153",
  },
  {
    // 7 — Teal
    primary:     "#14B8A6",
    primaryDark: "#0F766E",
    primaryMid:  "#2DD4BF",
    light:       "#F0FDFA",
    lighter:     "#F5FFFE",
    text:        "#0F766E",
    gradient:    "linear-gradient(135deg,#0F766E 0%,#14B8A6 50%,#2DD4BF 100%)",
    gradientBar: "linear-gradient(90deg,#14B8A6,#2DD4BF)",
    shadowRgb:   "20,184,166",
  },
  {
    // 8 — Deep cyan
    primary:     "#06B6D4",
    primaryDark: "#0E7490",
    primaryMid:  "#22D3EE",
    light:       "#ECFEFF",
    lighter:     "#F0FEFF",
    text:        "#0E7490",
    gradient:    "linear-gradient(135deg,#0E7490 0%,#06B6D4 50%,#22D3EE 100%)",
    gradientBar: "linear-gradient(90deg,#06B6D4,#22D3EE)",
    shadowRgb:   "6,182,212",
  },
  {
    // 9 — Lime green
    primary:     "#84CC16",
    primaryDark: "#3F6212",
    primaryMid:  "#A3E635",
    light:       "#F7FEE7",
    lighter:     "#FAFFF0",
    text:        "#3F6212",
    gradient:    "linear-gradient(135deg,#3F6212 0%,#84CC16 50%,#A3E635 100%)",
    gradientBar: "linear-gradient(90deg,#84CC16,#A3E635)",
    shadowRgb:   "132,204,22",
  },
  {
    // 10 — Fuchsia / magenta
    primary:     "#D946EF",
    primaryDark: "#86198F",
    primaryMid:  "#E879F9",
    light:       "#FDF4FF",
    lighter:     "#FEF8FF",
    text:        "#86198F",
    gradient:    "linear-gradient(135deg,#86198F 0%,#D946EF 50%,#E879F9 100%)",
    gradientBar: "linear-gradient(90deg,#D946EF,#E879F9)",
    shadowRgb:   "217,70,239",
  },
  {
    // 11 — Burnt sienna / terracotta
    primary:     "#DC6B19",
    primaryDark: "#92400E",
    primaryMid:  "#FB923C",
    light:       "#FFF7ED",
    lighter:     "#FFFAF5",
    text:        "#92400E",
    gradient:    "linear-gradient(135deg,#92400E 0%,#DC6B19 50%,#FB923C 100%)",
    gradientBar: "linear-gradient(90deg,#DC6B19,#FB923C)",
    shadowRgb:   "220,107,25",
  },
  {
    // 12 — Slate blue
    primary:     "#6366F1",
    primaryDark: "#4338CA",
    primaryMid:  "#A5B4FC",
    light:       "#EEF2FF",
    lighter:     "#F5F7FF",
    text:        "#4338CA",
    gradient:    "linear-gradient(135deg,#4338CA 0%,#6366F1 50%,#A5B4FC 100%)",
    gradientBar: "linear-gradient(90deg,#6366F1,#A5B4FC)",
    shadowRgb:   "99,102,241",
  },
  {
    // 13 — Deep rose
    primary:     "#F43F5E",
    primaryDark: "#9F1239",
    primaryMid:  "#FB7185",
    light:       "#FFF1F2",
    lighter:     "#FFF5F6",
    text:        "#9F1239",
    gradient:    "linear-gradient(135deg,#9F1239 0%,#F43F5E 50%,#FB7185 100%)",
    gradientBar: "linear-gradient(90deg,#F43F5E,#FB7185)",
    shadowRgb:   "244,63,94",
  },
  {
    // 14 — Steel blue / cobalt
    primary:     "#2563EB",
    primaryDark: "#1E3A8A",
    primaryMid:  "#60A5FA",
    light:       "#EFF6FF",
    lighter:     "#F5F9FF",
    text:        "#1E3A8A",
    gradient:    "linear-gradient(135deg,#1E3A8A 0%,#2563EB 50%,#60A5FA 100%)",
    gradientBar: "linear-gradient(90deg,#2563EB,#60A5FA)",
    shadowRgb:   "37,99,235",
  },
  {
    // 15 — Forest green
    primary:     "#16A34A",
    primaryDark: "#14532D",
    primaryMid:  "#4ADE80",
    light:       "#F0FDF4",
    lighter:     "#F5FFF8",
    text:        "#14532D",
    gradient:    "linear-gradient(135deg,#14532D 0%,#16A34A 50%,#4ADE80 100%)",
    gradientBar: "linear-gradient(90deg,#16A34A,#4ADE80)",
    shadowRgb:   "22,163,74",
  },
  {
    // 16 — Golden yellow
    primary:     "#CA8A04",
    primaryDark: "#713F12",
    primaryMid:  "#FACC15",
    light:       "#FEFCE8",
    lighter:     "#FFFEF5",
    text:        "#713F12",
    gradient:    "linear-gradient(135deg,#713F12 0%,#CA8A04 50%,#FACC15 100%)",
    gradientBar: "linear-gradient(90deg,#CA8A04,#FACC15)",
    shadowRgb:   "202,138,4",
  },
  {
    // 17 — Mauve / purple-pink
    primary:     "#A855F7",
    primaryDark: "#6B21A8",
    primaryMid:  "#C084FC",
    light:       "#FAF5FF",
    lighter:     "#FDF8FF",
    text:        "#6B21A8",
    gradient:    "linear-gradient(135deg,#6B21A8 0%,#A855F7 50%,#C084FC 100%)",
    gradientBar: "linear-gradient(90deg,#A855F7,#C084FC)",
    shadowRgb:   "168,85,247",
  },
  {
    // 18 — Charcoal / dark slate
    primary:     "#475569",
    primaryDark: "#1E293B",
    primaryMid:  "#94A3B8",
    light:       "#F1F5F9",
    lighter:     "#F8FAFC",
    text:        "#1E293B",
    gradient:    "linear-gradient(135deg,#1E293B 0%,#475569 50%,#94A3B8 100%)",
    gradientBar: "linear-gradient(90deg,#475569,#94A3B8)",
    shadowRgb:   "71,85,105",
  },
  {
    // 19 — Deep teal / dark cyan
    primary:     "#0D9488",
    primaryDark: "#134E4A",
    primaryMid:  "#34D399",
    light:       "#F0FDFA",
    lighter:     "#F5FFFD",
    text:        "#134E4A",
    gradient:    "linear-gradient(135deg,#134E4A 0%,#0D9488 50%,#34D399 100%)",
    gradientBar: "linear-gradient(90deg,#0D9488,#34D399)",
    shadowRgb:   "13,148,136",
  },
];

/** Returns the theme object for a given numeric project id */
function getTheme(projectId) {
  const idx = (Number(projectId) || 0) % THEMES.length;
  return THEMES[idx];
}

// ── Module Cards config (UNCHANGED logic, theme applied at render) ─────────────
const moduleCards = [
  {
    key: "reports",
    icon: <FileText size={20} />,
    title: "Reports",
    description: "View and manage project reports",
    path: (id) => `/engineer/project/${id}/reports`,
  },
  {
    key: "work-schedule",
    icon: <Calendar size={20} />,
    title: "Work Schedule",
    description: "Track daily work activities",
    path: (id) => `/engineer/project/${id}/work-schedule`,
  },
  
  {
    key: "Feedback",
    icon: <Star size={20} />,
    title: "Feedback",
    description: "Client feedback and reviews",
    path: (id) => `/engineer/project/${id}/testimonials`,
  },
  {
    key: "stakeholders",
    icon: <Users size={20} />,
    title: "Stakeholders",
    description: "Project stakeholder information",
    path: (id) => `/engineer/project/${id}/stakeholders`,
  },
];

// ── Status color helper (UNCHANGED) ──────────────────────────────────────────
function getStatusStyle(status = "") {
  const s = status.toLowerCase();
  if (s === "active")                             return { bg: "#F0FDF4", color: "#16A34A", dot: "#22C55E", label: "Active" };
  if (s === "completed")                          return { bg: "#F0FDF4", color: "#16A34A", dot: "#22C55E", label: "Completed" };
  if (s === "in progress" || s === "in_progress") return { bg: "#EDE6F7", color: "#5C2D91", dot: "#5C2D91", label: "In Progress" };
  if (s === "pending")                            return { bg: "#FFFBEB", color: "#B45309", dot: "#F59E0B", label: "Pending" };
  if (s === "rejected")                           return { bg: "#FEF2F2", color: "#DC2626", dot: "#EF4444", label: "Rejected" };
  return                                                 { bg: "#F8FAFC", color: "#64748B", dot: "#94A3B8", label: status || "Unknown" };
}

// ── Module Card — theme-driven accents ───────────────────────────────────────
function ModuleCard({ card, projectId, navigate, theme }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={() => navigate(card.path(projectId))}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        background: hovered ? theme.light : "#FFFFFF",
        border: `1.5px solid ${hovered ? theme.primary + "50" : "#E2E8F0"}`,
        borderRadius: 16,
        padding: "20px 18px 18px",
        cursor: "pointer",
        textAlign: "left",
        transition: "all 0.2s ease",
        boxShadow: hovered
          ? `0 8px 24px rgba(${theme.shadowRgb},0.18)`
          : "0 1px 4px rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-3px)" : "none",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* top accent line — theme primary */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: hovered ? theme.gradient : "transparent",
        borderRadius: "16px 16px 0 0", transition: "all 0.2s",
      }} />

      <div style={{
        width: 44, height: 44, borderRadius: 12,
        background: hovered ? theme.gradient : theme.light,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: hovered ? "#fff" : theme.primary,
        transition: "all 0.2s", flexShrink: 0,
        boxShadow: hovered ? `0 4px 12px rgba(${theme.shadowRgb},0.25)` : "none",
      }}>
        {card.icon}
      </div>

      <div style={{ flex: 1 }}>
        <div style={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 700, fontSize: 14,
          color: hovered ? theme.primary : "#0F172A",
          marginBottom: 4, transition: "color 0.2s",
        }}>
          {card.title}
        </div>
        <div style={{ fontSize: 12, color: "#64748B", lineHeight: 1.5 }}>
          {card.description}
        </div>
      </div>

      <div style={{
        alignSelf: "flex-end",
        display: "flex", alignItems: "center", gap: 4,
        fontSize: 12, fontWeight: 600,
        color: hovered ? theme.primary : "#94A3B8",
        transition: "all 0.2s",
      }}>
        Open <ArrowRight size={14} />
      </div>
    </button>
  );
}

// ── toPositiveInt (UNCHANGED) ─────────────────────────────────────────────────
const toPositiveInt = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return (Number.isFinite(n) && Number.isInteger(n) && n > 0) ? n : null;
};

// ── Main Component ────────────────────────────────────────────────────────────
function ProjectDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();

const [project,   setProject]   = useState(null);
  const [timeline,  setTimeline]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tlLoading, setTlLoading] = useState(true);
  const [totalArea, setTotalArea] = useState(null);

  const userName     = localStorage.getItem("name") || "Engineer";
  const userInitials = userName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);

  // ── ALL useEffect LOGIC COMPLETELY UNCHANGED ──────────────────────────────
  useEffect(() => {
    if (!id) return;
    setLoading(true);

    (async () => {
      try {
        const assignRes = await API.get("/engineers/me/projects");
        const assignments = assignRes.data?.projects || [];

        console.log("[ProjectDetail] looking for assignment id:", id);
        const matched = assignments.find(
          (a) =>
            String(a.id) === String(id) ||
            String(a.project_id) === String(id) ||
            String(a.real_project_id) === String(id)
        );

        if (!matched) {
          console.error("[ProjectDetail] no assignment found for id:", id,
            "available:", assignments.map(a => a.id));
          setProject(null);
          setLoading(false);
          return;
        }

console.log("[ProjectDetail] matched assignment:", JSON.stringify(matched));
        setTotalArea(matched.total_area_sqmtr ?? null);
        const resolvedId =
  toPositiveInt(matched.project_id) ||
  toPositiveInt(matched.real_project_id);

        if (resolvedId) {
          console.log("[ProjectDetail] resolved projects.id:", resolvedId);
         const projRes = await API.get(`/projects/${resolvedId}`);

console.log(
  "[ProjectDetail] Loaded project from API:",
  projRes.data.project
);

if (projRes.data?.success) {
  setProject(projRes.data.project);
} else {
  setProject(null);
}
        } else {
          const uidForFetch = matched.project_uid || null;
          console.log("[ProjectDetail] real_project_id null, fetching by uid:", uidForFetch);
          if (!uidForFetch) {
            setProject(null);
            setLoading(false);
            return;
          }
          const projRes = await API.get(`/projects/${uidForFetch}`);
          if (projRes.data?.success && projRes.data.project?.id) {
            setProject(projRes.data.project);
          } else {
            setProject(null);
          }
        }
      } catch (err) {
        console.error("[ProjectDetail] project resolution error:", err);
        setProject(null);
      } finally {
        setLoading(false);
      }
    })();

    setTlLoading(true);
    API.get(`/engineers/me/projects/${id}/timeline`)
      .then((res) => {
        if (res.data?.success) {
          const data = res.data.timeline ?? res.data.phases ?? res.data.data ?? [];
          setTimeline(Array.isArray(data) ? data : []);
        }
      })
      .catch((err) => console.error("Timeline fetch error:", err))
      .finally(() => setTlLoading(false));
  }, [id]);

  // ── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <DashboardShell
        portal="Engineer Portal" roleLabel="ENGINEER"
        title="Project Detail" subtitle="Loading project..."
        navItems={engineerNav} stats={engineerStats}
        user={{ initials: userInitials, name: userName, role: "Engineer" }}
      >
        <div style={{ textAlign:"center", padding:"80px 0", color:"#64748B" }}>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:12 }}><Hourglass size={40} /></div>
<div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:14, fontWeight:500 }}>Loading project details...</div>
        </div>
      </DashboardShell>
    );
  }

  // ── Not found ─────────────────────────────────────────────────────────────
  if (!project) {
    const fallbackTheme = THEMES[0];
    return (
      <DashboardShell
        portal="Engineer Portal" roleLabel="ENGINEER"
        title="Project Detail" subtitle="Not found"
        navItems={engineerNav} stats={engineerStats}
        user={{ initials: userInitials, name: userName, role: "Engineer" }}
      >
        <div style={{ textAlign:"center", padding:"80px 0", color:"#64748B" }}>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:12 }}><XCircle size={40} color="#EF4444" /></div>
        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:14, fontWeight:500 }}>
            Project not found or you do not have access.
          </div>
          <button
            onClick={() => navigate("/engineer/projects")}
            style={{
              marginTop:20, background: fallbackTheme.primary, color:"#fff", border:"none",
              borderRadius:10, padding:"10px 24px", fontSize:13, fontWeight:600,
              cursor:"pointer", fontFamily:"'Montserrat',sans-serif",
              display:"inline-flex", alignItems:"center", gap:6,
            }}
          ><ArrowLeft size={14} /> Back to Projects</button>
        </div>
      </DashboardShell>
    );
  }

  // ── Theme — derived from project.id once project is loaded ────────────────
  const theme = getTheme(project.id);

  // ── Derived values (UNCHANGED logic) ──────────────────────────────────────
  const projectUID    = project.project_uid || `PRJ-${project.id}`;
  const projectTitle  = project.title       || "Untitled Project";
  const projectStatus = project.status      || "Not Started";
  const statusStyle   = getStatusStyle(projectStatus);
  const startDate     = project.start_date
    ? new Date(project.start_date).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })
    : "—";
  const endDate       = project.end_date
    ? new Date(project.end_date).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" })
    : "—";
  const durationDays  = (project.start_date && project.end_date)
    ? Math.max(0, Math.round((new Date(project.end_date) - new Date(project.start_date)) / (1000*60*60*24)))
    : null;

  // ── Status KPI icon — derived once, replaces inline emoji ternary ─────────
  const statusIcon = (() => {
    const s = projectStatus.toLowerCase();
    if (s === "active" || s === "completed") return <CheckCircle2 size={16} color={statusStyle.color} />;
    if (s === "pending") return <Hourglass size={16} color={statusStyle.color} />;
    if (s === "rejected") return <XCircle size={16} color={statusStyle.color} />;
    return <BarChart3 size={16} color={statusStyle.color} />;
  })();

  return (
    <DashboardShell
      portal="Engineer Portal"
      roleLabel="ENGINEER"
      title={projectTitle}
      subtitle={`${projectUID} · ${projectStatus}`}
      navItems={engineerNav}
      stats={engineerStats}
      user={{ initials: userInitials, name: userName, role: "Engineer" }}
    >
      {/* ── Injected styles — theme CSS variables scoped to .pd-root ── */}
      <style>{`
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

        .pd-root {
          font-family: 'Montserrat', sans-serif;
          display: flex;
          flex-direction: column;
          gap: 16px;
          --theme-primary:     ${theme.primary};
          --theme-primary-dark:${theme.primaryDark};
          --theme-primary-mid: ${theme.primaryMid};
          --theme-light:       ${theme.light};
          --theme-lighter:     ${theme.lighter};
          --theme-text:        ${theme.text};
          --theme-gradient:    ${theme.gradient};
          --theme-gradient-bar:${theme.gradientBar};
          --theme-shadow:      rgba(${theme.shadowRgb},0.25);
        }

        .pd-card {
          background: #fff;
          border-radius: 16px;
          border: 1px solid #E2E8F0;
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.05);
        }
        .pd-card-inner { padding: 22px 24px; }

        /* ── HEADER — uses theme gradient ── */
        .pd-header {
          background: var(--theme-gradient);
          padding: 20px 24px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
        }
        .pd-header-left  { display: flex; flex-direction: column; gap: 6px; }
        .pd-uid-chip {
          display: inline-flex; align-items: center; gap: 6px;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: 6px; padding: 3px 10px;
          font-size: 11px; font-weight: 700;
          color: rgba(255,255,255,0.95); letter-spacing: 0.06em;
          width: fit-content;
        }
        .pd-title {
          font-size: 22px; font-weight: 800; color: #fff;
          letter-spacing: -0.02em; margin: 0; line-height: 1.2;
        }
        .pd-status-chip {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 12px; font-weight: 700;
          padding: 5px 12px; border-radius: 20px; width: fit-content;
        }
        .pd-header-right  { display: flex; gap: 8px; flex-wrap: wrap; }
       .pd-meta-pill {
  display: flex; flex-direction: column; align-items: center;
  background: rgba(0,0,0,0.22);
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 10px; padding: 10px 16px;
  min-width: 90px; text-align: center;
}
        .pd-meta-label {
          font-size: 9px; font-weight: 700;
          color: rgba(255,255,255,0.6);
          letter-spacing: 0.1em; text-transform: uppercase; margin-bottom: 4px;
        }
        .pd-meta-value { font-size: 13px; font-weight: 700; color: #fff; line-height: 1.2; }

        /* ── KPI STRIP — icon backgrounds use theme.light ── */
        .pd-kpi-strip {
          display: grid; grid-template-columns: repeat(4,1fr);
          border-top: 1px solid #F1F5F9;
        }
        .pd-kpi-cell {
          padding: 16px 20px; display: flex; align-items: center; gap: 12px;
          border-right: 1px solid #F1F5F9;
        }
        .pd-kpi-cell:last-child { border-right: none; }
        .pd-kpi-icon {
          width: 36px; height: 36px; border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .pd-kpi-label {
          font-size: 10px; color: #94A3B8; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 2px;
        }
        .pd-kpi-value { font-size: 13px; font-weight: 700; color: #0F172A; }

        /* ── TIMELINE — active/completed circles use theme ── */
        .pd-tl-header {
          display: flex; justify-content: space-between; align-items: center;
          margin-bottom: 20px; flex-wrap: wrap; gap: 10px;
        }
        .pd-tl-title    { font-size: 15px; font-weight: 700; color: #0F172A; }
        .pd-tl-sub      { font-size: 12px; color: #64748B; margin-top: 2px; }
        .pd-tl-legend   { display: flex; gap: 16px; }
        .pd-tl-leg      { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #64748B; }
        .pd-tl-leg-dot  { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
        .pd-tl-scroll   { overflow-x: auto; padding-bottom: 8px; }
        .pd-tl-track    { display: flex; align-items: flex-start; padding: 8px 4px; min-width: fit-content; }
        .pd-tl-step     { display: flex; flex-direction: column; align-items: center; width: 90px; flex-shrink: 0; cursor: default; }
        .pd-tl-circle   {
          width: 40px; height: 40px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700;
          transition: all 0.2s; flex-shrink: 0;
        }
        .pd-tl-name         { font-size: 11px; font-weight: 600; text-align: center; margin-top: 8px; line-height: 1.3; max-width: 84px; word-break: break-word; }
        .pd-tl-status-lbl   { font-size: 10px; text-align: center; margin-top: 3px; font-weight: 500; }
        .pd-tl-connector    { display: flex; align-items: center; flex-shrink: 0; margin-top: 20px; width: 32px; }
        .pd-tl-line         { height: 3px; width: 100%; border-radius: 2px; }

        /* ── MODULE GRID ── */
        .pd-mod-grid-top { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; margin-bottom: 14px; }
        .pd-mod-grid-bot { display: grid; grid-template-columns: repeat(4,1fr); gap: 14px; }

        /* ── SECTION LABEL ── */
        .pd-sec-label {
          font-size: 11px; font-weight: 700; color: #94A3B8;
          text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 14px;
        }

        /* ── BACK ROW — button border + text use theme ── */
        .pd-back-row    { display: flex; align-items: center; gap: 10px; margin-bottom: 4px; }
        .pd-back-btn {
          display: flex; align-items: center; gap: 6px;
          background: #fff;
          border: 1.5px solid #E2E8F0;
          border-radius: 8px; padding: 6px 14px;
          font-size: 12px; font-weight: 600;
          color: var(--theme-primary);
cursor: pointer; font-family: 'Montserrat',sans-serif;
          transition: all 0.18s;
        }
        .pd-back-btn:hover {
          border-color: var(--theme-primary);
          background: var(--theme-light);
        }
        .pd-breadcrumb         { font-size: 12px; color: #94A3B8; }
        .pd-breadcrumb strong  { color: #0F172A; font-weight: 600; }

        @media(max-width:900px){
          .pd-kpi-strip        { grid-template-columns: 1fr 1fr; }
          .pd-mod-grid-top     { grid-template-columns: 1fr 1fr; }
          .pd-mod-grid-bot     { grid-template-columns: 1fr 1fr; }
          .pd-header           { flex-direction: column; align-items: flex-start; }
          .pd-header-right     { width: 100%; }
          .pd-meta-pill        { flex: 1; }
        }
        @media(max-width:560px){
          .pd-kpi-strip        { grid-template-columns: 1fr 1fr; }
          .pd-kpi-cell         { padding: 14px 14px; }
          .pd-mod-grid-top     { grid-template-columns: 1fr; }
          .pd-mod-grid-bot     { grid-template-columns: 1fr; }
          .pd-card-inner       { padding: 16px 16px; }
          .pd-header           { padding: 16px 16px; }
          .pd-title            { font-size: 18px; }
        }
      `}</style>

      <div className="pd-root">

{/* ── BACK ROW ── */}
        <div className="pd-back-row">
          <button className="pd-back-btn" onClick={() => navigate("/engineer/projects")}><ArrowLeft size={14} /> Back</button>
          <div className="pd-breadcrumb">
            My Projects <span style={{ color: theme.primary }}>›</span> <strong>{projectTitle}</strong>
          </div>
        </div>

        {/* ══ PROJECT HEADER CARD ══ */}
        <div className="pd-card">
          {/* Gradient header — theme.gradient */}
          <div className="pd-header">
            <div className="pd-header-left">
              <div className="pd-uid-chip"><Hash size={12} /> {projectUID}</div>
              <h1 className="pd-title">{projectTitle}</h1>
              <div className="pd-status-chip" style={{ background: statusStyle.bg, color: statusStyle.color }}>
                <span style={{ width:7, height:7, borderRadius:"50%", background: statusStyle.dot, flexShrink:0 }} />
                {statusStyle.label}
              </div>
            </div>
<div className="pd-header-right">
              <div className="pd-meta-pill">
                <div className="pd-meta-label">Start Date</div>
                <div className="pd-meta-value">{startDate}</div>
              </div>
              <div className="pd-meta-pill">
                <div className="pd-meta-label">End Date</div>
                <div className="pd-meta-value">{endDate}</div>
              </div>
              {durationDays !== null && (
                <div className="pd-meta-pill">
                  <div className="pd-meta-label">Duration</div>
                  <div className="pd-meta-value">{durationDays}Days</div>
                </div>
              )}
              {totalArea !== null && (
                <div className="pd-meta-pill">
                  <div className="pd-meta-label">Total Area</div>
                  <div className="pd-meta-value">{totalArea} sqmtr</div>
                </div>
              )}
            </div>
          </div>

          {/* KPI strip — first cell uses theme colour, rest stay as-is */}
          <div className="pd-kpi-strip">
            <div className="pd-kpi-cell">
              <div className="pd-kpi-icon" style={{ background: theme.light, color: theme.primary }}><Hash size={16} /></div>
              <div>
                <div className="pd-kpi-label">Project ID</div>
                <div className="pd-kpi-value" style={{ color: theme.primary }}>{projectUID}</div>
              </div>
            </div>
            <div className="pd-kpi-cell">
              <div className="pd-kpi-icon" style={{ background: statusStyle.bg }}>{statusIcon}</div>
              <div>
                <div className="pd-kpi-label">Status</div>
                <div className="pd-kpi-value" style={{ color: statusStyle.color }}>{statusStyle.label}</div>
              </div>
            </div>
            <div className="pd-kpi-cell">
              {/* Start date icon uses theme light bg */}
              <div className="pd-kpi-icon" style={{ background: theme.light, color: theme.text }}><Calendar size={16} /></div>
              <div>
                <div className="pd-kpi-label">Start Date</div>
                <div className="pd-kpi-value" style={{ color: theme.text }}>{startDate}</div>
              </div>
            </div>
            <div className="pd-kpi-cell">
              {/* End date icon uses a slightly warmer tint — keep red for urgency */}
              <div className="pd-kpi-icon" style={{ background: "#FEF2F2", color: "#EF4444" }}><Flag size={16} /></div>
              <div>
                <div className="pd-kpi-label">End Date</div>
                <div className="pd-kpi-value" style={{ color: "#EF4444" }}>{endDate}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ══ TIMELINE CARD ══ */}
        <div className="pd-card">
          <div className="pd-card-inner">
            <div className="pd-tl-header">
              <div>
                <div className="pd-tl-title">Project Timeline</div>
                <div className="pd-tl-sub">Phase-by-phase progress overview</div>
              </div>
              <div className="pd-tl-legend">
                {/* Legend dot for "In Progress" uses theme primary */}
                <div className="pd-tl-leg">
                  <div className="pd-tl-leg-dot" style={{ background:"#22C55E" }} />
                  Completed
                </div>
                <div className="pd-tl-leg">
                  <div className="pd-tl-leg-dot" style={{ background: theme.primary }} />
                  In Progress
                </div>
                <div className="pd-tl-leg">
                  <div className="pd-tl-leg-dot" style={{ background:"#E2E8F0", border:"1px solid #CBD5E1" }} />
                  Upcoming
                </div>
              </div>
            </div>

            {tlLoading ? (
              <div style={{ textAlign:"center", padding:"28px 0", color:"#94A3B8", fontSize:13 }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}><Hourglass size={24} /></div>Loading timeline...
              </div>
            ) : timeline.length === 0 ? (
              <div style={{ textAlign:"center", padding:"36px 0", color:"#94A3B8", fontSize:13 }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:8 }}><ClipboardList size={32} /></div>
                No timeline phases found for this project.
              </div>
            ) : (
              <div className="pd-tl-scroll">
                <div className="pd-tl-track">
                  {timeline.map((phase, index) => {
                    // ── ALL TIMELINE LOGIC UNCHANGED ─────────────────────────
                    const rawStatus    = (phase.status || "").toLowerCase().replace(/_/g, " ").trim();
                    const isCompleted  = rawStatus === "completed";
                    const isInProgress = rawStatus === "in progress" || rawStatus === "in_progress" || rawStatus === "active";

                    // Completed stays green (universal ✓ signal)
                    // In Progress uses theme.primary instead of hardcoded purple
                    const circleBg     = isCompleted ? "#22C55E" : isInProgress ? theme.primary    : "#F1F5F9";
                    const circleBorder = isCompleted ? `2px solid #16A34A` : isInProgress ? `2px solid ${theme.primary}` : "2px solid #CBD5E1";
                    const circleColor  = isCompleted ? "#fff" : isInProgress ? "#fff" : "#94A3B8";
                    const circleIcon   = isCompleted ? <CheckCircle2 size={18} /> : String(index + 1);
                    const nameCss      = isCompleted ? "#16A34A" : isInProgress ? theme.primary : "#64748B";
                    const statusLbl    = isCompleted ? "Done" : isInProgress ? "Active" : "Pending";
                    const statusLblCss = isCompleted ? "#22C55E" : isInProgress ? theme.primary : "#94A3B8";
                    const lineBg       = isCompleted
                      ? "linear-gradient(90deg,#22C55E,#4ADE80)"
                      : "linear-gradient(90deg,#E2E8F0,#E2E8F0)";

                    const displayName  = phase.name || phase.title || phase.code || `Phase ${index + 1}`;

                    return (
                      <div key={phase.id ?? index} style={{ display:"flex", alignItems:"flex-start" }}>
                        <div className="pd-tl-step">
                          <div className="pd-tl-circle" style={{
                            background: circleBg,
                            border:     circleBorder,
                            color:      circleColor,
                            boxShadow:  isCompleted
                              ? "0 4px 12px rgba(34,197,94,0.3)"
                              : isInProgress
                              ? `0 4px 12px rgba(${theme.shadowRgb},0.3)`
                              : "none",
                            fontSize: isCompleted ? 16 : 13,
                          }}>
                            {circleIcon}
                          </div>
                          <div className="pd-tl-name"      style={{ color: nameCss }}>{displayName}</div>
                          <div className="pd-tl-status-lbl" style={{ color: statusLblCss }}>{statusLbl}</div>
                        </div>
                        {index < timeline.length - 1 && (
                          <div className="pd-tl-connector">
                            <div className="pd-tl-line" style={{ background: lineBg }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ══ PROJECT MODULES ══ */}
        <div className="pd-card">
          <div className="pd-card-inner">
            <div className="pd-sec-label">Project Modules</div>
            {/* First 4 modules */}
            <div className="pd-mod-grid-top">
              {moduleCards.slice(0, 4).map((card) => (
                <ModuleCard key={card.key} card={card} projectId={id} navigate={navigate} theme={theme} />
              ))}
            </div>
            {/* 5th module (Stakeholders) */}
            {moduleCards.slice(4).length > 0 && (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
                {moduleCards.slice(4).map((card) => (
                  <ModuleCard key={card.key} card={card} projectId={id} navigate={navigate} theme={theme} />
                ))}
              </div>
            )}
          </div>
        </div>

      </div>
    </DashboardShell>
  );
}

export default ProjectDetail;